import { useCallback, useState } from "react";
import axios, { AxiosResponse } from "axios";
import toast from "react-hot-toast";
import { HOME_TILE_CONFIG_APIS } from "@/api_framework/api_config";
import {
  DEFAULT_RIBBON_STYLE,
  HomeTileConfigStatus,
  HomeTileConfigWireStatus,
  IHomeTileConfig,
  IHomeTileConfigApiRow,
  IHomeTileConfigMutationResponse,
  IHomeTileConfigSaveBody,
  IHomeTileConfigUpdateBody,
  IHomeTileDetailResponse,
  IRibbonStyle,
  RIBBON_POSITIONS,
  RibbonPosition,
} from "@/api_framework/api_modals/HomeTileConfig";
import { useQuickCommerce } from "@/app/hooks/useQuickCommerce";
import { buildAuthHeaders, readErrorMessage, tileUrl } from "./homeTileApiUtils";

export const toWireStatus = (status: HomeTileConfigStatus): HomeTileConfigWireStatus =>
  status === "ACTIVE" ? "active" : "inactive";

const normalizeConfigStatus = (value: string | null | undefined): HomeTileConfigStatus =>
  value?.trim().toLowerCase() === "active" ? "ACTIVE" : "INACTIVE";

const toRibbonPosition = (value: string | null | undefined): RibbonPosition =>
  RIBBON_POSITIONS.find((position) => position === value) ?? DEFAULT_RIBBON_STYLE.position;

const toRibbonStyle = (ribbon: IRibbonStyle | null): IRibbonStyle => ({
  bg_color: ribbon?.bg_color || DEFAULT_RIBBON_STYLE.bg_color,
  text_color: ribbon?.text_color || DEFAULT_RIBBON_STYLE.text_color,
  border_color: ribbon?.border_color || DEFAULT_RIBBON_STYLE.border_color,
  position: toRibbonPosition(ribbon?.position),
});

const toHomeTileConfig = (row: IHomeTileConfigApiRow, tileId: string): IHomeTileConfig => {
  const configuration = row.configuration ?? {};
  return {
    id: row.config_id ?? row.id ?? "",
    tile_id: row.tile_id || tileId,
    status: normalizeConfigStatus(row.status),
    ribbon_text: row.ribbon_text ?? "",
    ribbon: toRibbonStyle(row.ribbon ?? null),
    persona_rule: configuration.persona_rule ?? null,
    overrides_dynamic: Boolean(row.overrides_dynamic),
    is_dynamic_enabled: Boolean(configuration.is_dynamic_enabled),
    membership_ribbon_format: configuration.membership_ribbon_format ?? null,
    resolution_order: configuration.resolution_order ?? null,
    created_at: row.created_at ?? null,
    updated_at: row.updated_at ?? null,
    updated_by: row.updated_by ?? null,
  };
};

export const useHomeTileConfigAPI = () => {
  const quickCommerce = useQuickCommerce();
  const [configList, setConfigList] = useState<IHomeTileConfig[]>([]);
  const [isListLoading, setIsListLoading] = useState<boolean>(true);
  const [listError, setListError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const accessTokenKey = quickCommerce.localStorage.ACCESS_TOKEN.key;
  const authHeaders = useCallback(() => buildAuthHeaders(accessTokenKey), [accessTokenKey]);

  const fetchTileConfigs = useCallback(
    async (tileId: string): Promise<IHomeTileConfig[] | null> => {
      setIsListLoading(true);
      try {
        const res: AxiosResponse<IHomeTileDetailResponse> = await axios.get(
          tileUrl(HOME_TILE_CONFIG_APIS.TILES, tileId),
          { headers: authHeaders() },
        );
        if (!res.data?.status) {
          setConfigList([]);
          setListError(res.data?.message || "Couldn't load configurations.");
          return null;
        }
        const rows = res.data.data?.configurations ?? res.data.data?.configs ?? [];
        const configs = rows
          .filter((row) => row.status?.trim().toLowerCase() !== "deleted")
          .map((row) => toHomeTileConfig(row, tileId))
          .filter((config) => Boolean(config.id));
        setConfigList(configs);
        setListError(null);
        return configs;
      } catch (error) {
        setConfigList([]);
        setListError(readErrorMessage(error) || "Couldn't load configurations.");
        return null;
      } finally {
        setIsListLoading(false);
      }
    },
    [authHeaders],
  );

  const saveConfig = useCallback(
    async (body: IHomeTileConfigSaveBody | IHomeTileConfigUpdateBody, configId?: string): Promise<boolean> => {
      setIsSaving(true);
      try {
        const res: AxiosResponse<IHomeTileConfigMutationResponse> = configId
          ? await axios.put(tileUrl(HOME_TILE_CONFIG_APIS.CONFIGS, configId), body, {
              headers: authHeaders(),
            })
          : await axios.post(HOME_TILE_CONFIG_APIS.CONFIGS, body, { headers: authHeaders() });

        if (!res.data?.status) {
          toast.error(res.data?.message || "Failed to save the tile configuration.");
          return false;
        }
        toast.success(configId ? "Tile configuration updated." : "Tile configuration created.");
        return true;
      } catch (error) {
        toast.error(readErrorMessage(error) || "Failed to save the tile configuration. Please try again.");
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [authHeaders],
  );

  const deleteConfig = useCallback(
    async (configId: string): Promise<boolean> => {
      setIsDeleting(true);
      try {
        const body: IHomeTileConfigUpdateBody = { status: "deleted" };
        const res: AxiosResponse<IHomeTileConfigMutationResponse> = await axios.put(
          tileUrl(HOME_TILE_CONFIG_APIS.CONFIGS, configId),
          body,
          { headers: authHeaders() },
        );
        if (!res.data?.status) {
          toast.error(res.data?.message || "Failed to delete the rule.");
          return false;
        }
        setConfigList((current) => current.filter((config) => config.id !== configId));
        toast.success("Rule deleted.");
        return true;
      } catch (error) {
        toast.error(readErrorMessage(error) || "Failed to delete the rule. Please try again.");
        return false;
      } finally {
        setIsDeleting(false);
      }
    },
    [authHeaders],
  );

  return {
    configList,
    isListLoading,
    listError,
    isSaving,
    isDeleting,
    fetchTileConfigs,
    saveConfig,
    deleteConfig,
  };
};
